// Lightweight global app-settings store. One axios poll every 60 seconds —
// shared across every subscriber so each component can call useAppSettings()
// without duplicating network traffic. Admins update via POST /admin/app/settings
// which calls `mutateAppSettings()` to push the new values into the store.
//
// Defaults match the backend DEFAULTS so first paint always has a reasonable
// answer even before the first network response lands.

import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

const DEFAULTS = {
  liveActivityTickerEnabled: true,
  liveActivityWidgetEnabled: true,
  liveActivityToastEnabled: true,
  // iter108 — payment flows removed. razorpayLinks field kept for backward
  // compatibility with any persisted admin settings document but is no
  // longer read by any UI code.
  razorpayLinks: {},
  // iter139 — admin-controlled "For our teachers" Odia message. Defaults
  // mirror the backend so first paint never shows an empty card.
  teachersMessageEnabled: true,
  teachersMessageContent: null,
};

let cache = { ...DEFAULTS };
let cacheFetched = false;
const subscribers = new Set();
let pollTimer = null;

function notify() {
  subscribers.forEach((cb) => {
    try { cb({ ...cache }); } catch { /* ignore */ }
  });
}

async function fetchNow() {
  // iter150-review — skip tick entirely if we're hidden or offline.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  // Only fetch when at least one component is subscribed and we have a token
  // in localStorage (settings are auth-required).
  try {
    const r = await axios.get(`${API}/app/settings`);
    cache = { ...DEFAULTS, ...(r.data || {}) };
    cacheFetched = true;
    notify();
  } catch (e) {
    // Silent — keep last-known cache (or defaults). If 401 the user isn't
    // logged in; we'll retry on next subscriber.
  }
}

// iter150-review — polling: only tick while visible + online. 60 s cadence.
// Also ticks immediately on visibilitychange → visible and on `online`.
function startPollingIfNeeded() {
  if (pollTimer || subscribers.size === 0) return;
  fetchNow();
  pollTimer = setInterval(() => { fetchNow(); }, POLL_MS);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
  if (typeof window !== "undefined") window.addEventListener("online", onOnline);
}

function stopPollingIfIdle() {
  if (pollTimer && subscribers.size === 0) {
    clearInterval(pollTimer);
    pollTimer = null;
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    if (typeof window !== "undefined") window.removeEventListener("online", onOnline);
  }
}

function onVisibility() { if (document.visibilityState === "visible") fetchNow(); }
function onOnline() { fetchNow(); }

export function useAppSettings() {
  const [state, setState] = useState({ ...cache });

  useEffect(() => {
    const cb = (next) => setState(next);
    subscribers.add(cb);
    startPollingIfNeeded();
    if (cacheFetched) setState({ ...cache });
    return () => {
      subscribers.delete(cb);
      stopPollingIfIdle();
    };
  }, []);

  return state;
}

// Mutator — used by the admin settings panel after a successful POST so the
// UI flips instantly without waiting for the next poll.
export function mutateAppSettings(patch) {
  cache = { ...cache, ...patch };
  cacheFetched = true;
  notify();
}
